import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, FileText } from 'lucide-react';
import { useAppContext } from '@/contexts/AppContext';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/use-toast';

interface ShiftData {
  startTime: string;
  startOdo: number;
  endTime?: string;
  endOdo?: number;
  breakTime?: number;
}

interface ShiftSummaryProps {
  shifts: ShiftData[];
  driverName: string;
  onExportExcel: () => void;
  onExportPDF: () => void;
}

const ShiftSummary: React.FC<ShiftSummaryProps> = ({ 
  shifts, 
  driverName, 
  onExportExcel, 
  onExportPDF 
}) => {
  const { drivers, timesheets } = useAppContext();
  const [realShifts, setRealShifts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const currentDriver = drivers.find(d => d.name === driverName);

  useEffect(() => {
    loadShiftData();
  }, [driverName]);

  const loadShiftData = async () => {
    if (!currentDriver) {
      setLoading(false);
      return;
    }

    try {
      // Get shifts from driver_shifts table
      const { data: shiftsData, error } = await supabase
        .from('driver_shifts')
        .select('*')
        .eq('driver_id', currentDriver.name)
        .order('shift_date', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Error loading shifts:', error);
        // Fall back to context data
        const driverTimesheets = timesheets.filter(t => t.driverId === currentDriver.id);
        setRealShifts(driverTimesheets);
      } else if (shiftsData) {
        setRealShifts(shiftsData);
      }
    } catch (error) {
      console.error('Error loading shift data:', error);
      // Fall back to context data
      const driverTimesheets = timesheets.filter(t => t.driverId === currentDriver?.id);
      setRealShifts(driverTimesheets);
    } finally {
      setLoading(false);
    }
  };

  const calculateShiftHours = (shift: any) => {
    let startTime, endTime;
    
    if (shift.login_time && shift.logoff_time) {
      // From timesheets
      startTime = shift.login_time;
      endTime = shift.logoff_time;
    } else if (shift.start_time && shift.end_time) {
      // From driver_shifts
      startTime = shift.start_time;
      endTime = shift.end_time;
    } else {
      return { total: 0, onShift: 0, onBreak: 0 };
    }
    
    const start = new Date(`2024-01-01T${startTime}`);
    const end = new Date(`2024-01-01T${endTime}`);
    const totalMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
    const breakMinutes = shift.break_time || shift.breaks * 60 || 0;
    
    return {
      total: totalMinutes / 60,
      onShift: (totalMinutes - breakMinutes) / 60,
      onBreak: breakMinutes / 60
    };
  };

  const totals = realShifts.reduce(
    (acc, shift) => {
      const hours = calculateShiftHours(shift);
      acc.totalHours += hours.total;
      acc.onShiftHours += hours.onShift;
      acc.onBreakHours += hours.onBreak;
      return acc;
    },
    { totalHours: 0, onShiftHours: 0, onBreakHours: 0 }
  );

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardContent className="p-6">
          <div className="text-center">Loading shift data...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-xl text-blue-800">
            Shift Summary - {driverName}
          </CardTitle>
          <div className="flex gap-2">
            <Button onClick={onExportExcel} size="sm" variant="outline">
              <Download className="w-4 h-4 mr-1" />
              Excel
            </Button>
            <Button onClick={onExportPDF} size="sm" variant="outline">
              <FileText className="w-4 h-4 mr-1" />
              PDF
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {totals.totalHours.toFixed(1)}
            </div>
            <div className="text-sm text-gray-600">Total Hours</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {totals.onShiftHours.toFixed(1)}
            </div>
            <div className="text-sm text-gray-600">On Shift</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {totals.onBreakHours.toFixed(1)}
            </div>
            <div className="text-sm text-gray-600">On Break</div>
          </div>
        </div>
        
        <div className="space-y-2">
          <h4 className="font-semibold text-gray-700">Recent Shifts:</h4>
          {realShifts.length > 0 ? (
            realShifts.map((shift, index) => {
              const hours = calculateShiftHours(shift);
              const startTime = shift.login_time || shift.start_time || 'N/A';
              const endTime = shift.logoff_time || shift.end_time || 'In Progress';
              
              return (
                <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <span className="text-sm">
                    {startTime} - {endTime}
                  </span>
                  <div className="text-sm text-gray-600">
                    {hours.total.toFixed(1)}h total | {hours.onShift.toFixed(1)}h work | {hours.onBreak.toFixed(1)}h break
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-4 text-gray-500">
              No shift data found
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ShiftSummary;